/* Created by Interactive 63 - https://interactive63.com */
/**
 * The Scene settings dialog: duration, delay, repeat, direction, fill mode, start trigger and
 * the reduced motion preference. Each time the dialog is opened, everything changed in it is one
 * undo step.
 */
import { triggerNeedsRuntime } from '../../core/compiler/triggers';
import type { SceneSettings, Trigger } from '../../core/model/types';
import { preferences } from '../preferences';
import { selectElements, setExportSettings, setSceneSettings } from '../store/actions';
import { setDuration } from '../store/property-actions';
import type { Store } from '../store/store';
import { formatTime, parseTime } from '../time-format';
import { h, svgEl } from '../ui/dom';
import { jsBadge } from '../ui/js-badge';
import { toast } from '../ui/toast';

type Direction = SceneSettings['direction'];
type Fill = SceneSettings['fillMode'];
type Reduced = SceneSettings['reducedMotion'];

const DIRECTIONS: [Direction, string, string][] = [
  ['normal', 'Normal', 'Plays forwards every time'],
  ['reverse', 'Reverse', 'Plays backwards every time'],
  ['alternate', 'Alternate', 'Plays forwards, then backwards'],
  ['alternate-reverse', 'Alternate reverse', 'Plays backwards, then forwards'],
];

const FILLS: [Fill, string][] = [
  ['forwards', 'Hold the end pose after playing'],
  ['backwards', 'Show the start pose during the delay'],
  ['both', 'Both'],
  ['none', 'None'],
];

const REDUCED: [Reduced, string][] = [
  ['final-frame', 'Show the end pose'],
  ['first-frame', 'Show the start pose'],
  ['ignore', 'Play anyway'],
];

type StartChoice =
  | 'auto'
  | 'hover-pause'
  | 'hover-reverse'
  | 'hover-restart'
  | 'click-toggle'
  | 'click-restart'
  | 'click-once'
  | 'in-view'
  | 'scroll';

const START_OPTIONS: [StartChoice, string][] = [
  ['auto', 'Automatically'],
  ['hover-pause', 'While hovering, pausing when the pointer leaves'],
  ['hover-reverse', 'On hover (play forwards, reverse when the pointer leaves)'],
  ['hover-restart', 'On hover (restart each time)'],
  ['click-toggle', 'On click (toggle)'],
  ['click-restart', 'On click (restart)'],
  ['click-once', 'On click (play once)'],
  ['in-view', 'When scrolled into view'],
  ['scroll', 'Scrub with scroll'],
];

/** Starting points for the triggers that have settings. */
const DEFAULT_IN_VIEW: Trigger = { type: 'inView', threshold: 0.3, once: true, onLeave: 'pause' };
const DEFAULT_SCROLL: Trigger = { type: 'scroll', startOffset: 100, endOffset: 0, smoothing: 0 };

/** Which start option a trigger corresponds to. */
function choiceOf(trigger: Trigger): StartChoice {
  switch (trigger.type) {
    case 'auto':
      return 'auto';
    case 'hover':
      return trigger.mode === 'pause-resume'
        ? 'hover-pause'
        : trigger.mode === 'play-reverse'
          ? 'hover-reverse'
          : 'hover-restart';
    case 'click':
      return trigger.mode === 'toggle'
        ? 'click-toggle'
        : trigger.mode === 'restart'
          ? 'click-restart'
          : 'click-once';
    case 'inView':
      return 'in-view';
    case 'scroll':
      return 'scroll';
  }
}

/** The trigger a start option stands for. */
function triggerFor(choice: StartChoice): Trigger {
  switch (choice) {
    case 'auto':
      return { type: 'auto' };
    case 'hover-pause':
      return { type: 'hover', mode: 'pause-resume' };
    case 'hover-reverse':
      return { type: 'hover', mode: 'play-reverse' };
    case 'hover-restart':
      return { type: 'hover', mode: 'restart' };
    case 'click-toggle':
      return { type: 'click', mode: 'toggle' };
    case 'click-restart':
      return { type: 'click', mode: 'restart' };
    case 'click-once':
      return { type: 'click', mode: 'play-once' };
    case 'in-view':
      return DEFAULT_IN_VIEW;
    case 'scroll':
      return DEFAULT_SCROLL;
  }
}

let openings = 0;

export class SceneSettingsDialog {
  private readonly dialog: HTMLDialogElement;
  private readonly body: HTMLElement;
  private key = '';

  /** Creates the dialog and attaches it to the page. */
  constructor(private readonly store: Store) {
    this.body = h('div', { class: 'scene-settings' });
    const close = h('button', { type: 'button', class: 'primary' }, 'Done');
    close.addEventListener('click', () => {
      this.dialog.close();
    });
    this.dialog = h(
      'dialog',
      { class: 'settings-dialog', 'aria-label': 'Scene settings' },
      h('header', { class: 'dialog-header' }, h('h3', null, 'Scene settings')),
      this.body,
      h('div', { class: 'dialog-actions' }, close),
    );
    document.body.append(this.dialog);
  }

  /** Opens the dialog. */
  open(): void {
    openings += 1;
    // Drag keys have no time window, so a whole visit to the dialog is one undo step.
    this.key = `drag:scene-settings:${openings}`;
    this.render();
    if (!this.dialog.open) this.dialog.showModal();
  }

  /** Writes a change to the scene. */
  private change(partial: Partial<SceneSettings>): void {
    setSceneSettings(this.store, partial, { coalesceKey: this.key });
  }

  /** Picks a start option. Options that need the runtime switch the export to CSS + JS. */
  private chooseStart(choice: StartChoice): void {
    const trigger = triggerFor(choice);
    this.change({ trigger });
    if (
      triggerNeedsRuntime(trigger) &&
      this.store.getState().project.export.codeType !== 'css-js'
    ) {
      setExportSettings(this.store, { codeType: 'css-js' }, this.key);
      toast.info(
        'This start option uses the SVG Tweener JS runtime, so exports now include a small script.',
      );
    }
  }

  /** A range slider with a live value, for trigger settings. */
  private slider(
    label: string,
    value: number,
    range: { min: number; max: number; step: number },
    set: (value: number) => void,
    format: (value: number) => string = String,
  ): HTMLElement {
    const input = h('input', { type: 'range', ...range, value, 'aria-label': label });
    const readout = h('output', null, format(value));
    input.addEventListener('input', () => {
      readout.textContent = format(Number(input.value));
      set(Number(input.value));
    });
    return h(
      'label',
      { class: 'settings-row' },
      h('span', null, label),
      h('span', { class: 'settings-inline' }, input, readout),
    );
  }

  /** A small diagram of the viewport with the scroll start and end lines. */
  private scrollDiagram(
    start: number,
    end: number,
  ): { el: SVGSVGElement; place: (s: number, e: number) => void } {
    const startLine = svgEl('line', { class: 'diagram-line diagram-start', x1: 0, x2: 90 });
    const endLine = svgEl('line', { class: 'diagram-line diagram-end', x1: 0, x2: 90 });
    const startText = svgEl('text', { x: 92, class: 'diagram-text' }, 'start');
    const endText = svgEl('text', { x: 92, class: 'diagram-text' }, 'end');
    const place = (s: number, e: number): void => {
      const y = (pct: number): number => 4 + (pct / 100) * 112;
      startLine.setAttribute('y1', String(y(s)));
      startLine.setAttribute('y2', String(y(s)));
      endLine.setAttribute('y1', String(y(e)));
      endLine.setAttribute('y2', String(y(e)));
      startText.setAttribute('y', String(y(s) + 4));
      endText.setAttribute('y', String(y(e) + 4));
    };
    place(start, end);
    const svg = svgEl(
      'svg',
      {
        class: 'scroll-diagram',
        viewBox: '0 0 130 120',
        width: 130,
        height: 120,
        role: 'img',
        'aria-label': 'The viewport with the start and end lines',
      },
      svgEl('rect', { x: 0, y: 4, width: 90, height: 112, class: 'diagram-viewport' }),
      startLine,
      endLine,
      startText,
      endText,
    );
    return { el: svg, place };
  }

  /** The settings for the chosen trigger, if it has any. */
  private triggerOptions(): HTMLElement | null {
    const { scene } = this.store.getState().project;
    const trigger = scene.trigger;
    const section = h('div', { class: 'trigger-options' });
    const update = (next: Trigger): void => {
      this.change({ trigger: next });
    };
    if (trigger.type === 'hover' && trigger.mode === 'play-reverse') {
      return scene.iterations === 'infinite'
        ? h(
            'p',
            { class: 'settings-note' },
            'Reversing on leave has no end to reverse to when the scene repeats forever. Use a finite repeat count.',
          )
        : null;
    }
    if (trigger.type === 'inView') {
      let current = trigger;
      const once = h('input', { type: 'checkbox', checked: current.once });
      once.addEventListener('change', () => {
        current = { ...current, once: once.checked };
        update(current);
      });
      const leave = h(
        'select',
        { 'aria-label': 'When it leaves view' },
        ...(['pause', 'reset', 'continue'] as const).map((v) =>
          h(
            'option',
            { value: v, selected: v === current.onLeave },
            v === 'pause' ? 'Pause' : v === 'reset' ? 'Go back to the start' : 'Keep playing',
          ),
        ),
      );
      leave.addEventListener('change', () => {
        current = { ...current, onLeave: leave.value as typeof current.onLeave };
        update(current);
      });
      section.append(
        this.slider('Threshold', current.threshold, { min: 0, max: 1, step: 0.05 }, (value) => {
          current = { ...current, threshold: value };
          update(current);
        }),
        h('label', { class: 'settings-check' }, once, ' Play once'),
        h('label', { class: 'settings-row' }, h('span', null, 'When it leaves view'), leave),
      );
      return section;
    }
    if (trigger.type === 'scroll') {
      let current = trigger;
      const diagram = this.scrollDiagram(current.startOffset, current.endOffset);
      /** Moves the diagram's lines to the current offsets. */
      const redraw = (): void => {
        diagram.place(current.startOffset, current.endOffset);
      };
      const percent = (value: number): string => `${value}%`;
      section.append(
        diagram.el,
        this.slider(
          'Start line',
          current.startOffset,
          { min: 0, max: 100, step: 5 },
          (value) => {
            current = { ...current, startOffset: value };
            redraw();
            update(current);
          },
          percent,
        ),
        this.slider(
          'End line',
          current.endOffset,
          { min: 0, max: 100, step: 5 },
          (value) => {
            current = { ...current, endOffset: value };
            redraw();
            update(current);
          },
          percent,
        ),
        this.slider('Smoothing', current.smoothing, { min: 0, max: 0.95, step: 0.05 }, (value) => {
          current = { ...current, smoothing: value };
          update(current);
        }),
        h(
          'p',
          { class: 'settings-note' },
          'Progress is 0 when the top of the graphic reaches the start line and 1 when its bottom reaches the end line.',
        ),
      );
      return section;
    }
    return null;
  }

  /** A group of radio buttons for one setting. */
  private radios<T extends string>(
    name: string,
    options: [T, string, string?, boolean?][],
    current: T,
    set: (value: T) => void,
  ): HTMLElement {
    return h(
      'div',
      { class: 'radio-group', role: 'radiogroup', 'aria-label': name },
      ...options.map(([value, label, hint, needsJs]) => {
        const input = h('input', { type: 'radio', name, value, checked: value === current });
        input.addEventListener('change', () => {
          if (input.checked) set(value);
        });
        return h(
          'label',
          { class: 'radio-row' },
          input,
          h(
            'span',
            null,
            label,
            hint ? h('small', null, ` ${hint}`) : null,
            needsJs ? jsBadge() : null,
          ),
        );
      }),
    );
  }

  /** The line about tracks with their own playback, with a link to the first of them. */
  private playbackSummary(): HTMLElement | null {
    const { tracks } = this.store.getState().project;
    const repeating = tracks.filter((t) => t.repeat !== undefined);
    const once = tracks.filter((t) => t.restartWithScene === false);
    if (repeating.length === 0 && once.length === 0) return null;
    const parts = [
      repeating.length > 0
        ? `${repeating.length} track${repeating.length === 1 ? '' : 's'} repeat`
        : '',
      once.length > 0 ? `${once.length} play${once.length === 1 ? 's' : ''} once` : '',
    ].filter((p) => p !== '');
    const first = repeating[0] ?? once[0];
    const link = h('button', { type: 'button', class: 'link-button' }, 'Show in timeline');
    link.addEventListener('click', () => {
      if (first) selectElements(this.store, [first.elementId]);
      this.dialog.close();
    });
    return h('p', { class: 'settings-note' }, `${parts.join(', ')}. `, link);
  }

  /** Builds the form from the current scene settings. */
  private render(): void {
    const { scene } = this.store.getState().project;

    const duration = h('input', {
      class: 'scene-duration',
      'aria-label': 'Duration',
      autocomplete: 'off',
      value: formatTime(scene.duration),
    });
    const scale = h('input', { type: 'checkbox', checked: preferences.scaleKeyframes });
    scale.addEventListener('change', () => {
      preferences.scaleKeyframes = scale.checked;
    });
    duration.addEventListener('change', () => {
      const ms = parseTime(duration.value);
      if (ms !== null) {
        setDuration(this.store, ms, {
          scale: scale.checked,
          confirmRemoval: (lost) =>
            window.confirm(
              `${lost} keyframe${lost === 1 ? ' is' : 's are'} after the new end time and will be removed. Continue?`,
            ),
        });
      }
      duration.value = formatTime(this.store.getState().project.scene.duration);
    });

    const delay = h('input', {
      type: 'number',
      min: 0,
      max: 60000,
      step: 10,
      value: scene.delay,
      'aria-label': 'Delay in milliseconds',
    });
    delay.addEventListener('change', () => {
      this.change({ delay: Math.max(0, Math.round(Number(delay.value) || 0)) });
    });

    const infinite = scene.iterations === 'infinite';
    const repeat = h('input', {
      type: 'number',
      min: 1,
      max: 1000,
      step: 1,
      value: infinite ? 1 : scene.iterations,
      disabled: infinite,
      'aria-label': 'Repeat count',
    });
    const forever = h('input', { type: 'checkbox', checked: infinite, 'aria-label': 'Infinite' });
    repeat.addEventListener('change', () => {
      this.change({
        iterations: Math.min(1000, Math.max(1, Math.round(Number(repeat.value) || 1))),
      });
    });
    forever.addEventListener('change', () => {
      repeat.disabled = forever.checked;
      this.change({
        iterations: forever.checked
          ? 'infinite'
          : Math.max(1, Math.round(Number(repeat.value) || 1)),
      });
    });

    const trigger = scene.trigger;
    const startOptions = START_OPTIONS.map(
      ([value, label]): [StartChoice, string, undefined, boolean] => [
        value,
        label,
        undefined,
        triggerNeedsRuntime(triggerFor(value)),
      ],
    );

    this.body.replaceChildren(
      ...[
        h('label', { class: 'settings-row' }, h('span', null, 'Duration'), duration),
        h('label', { class: 'settings-check' }, scale, ' Scale keyframes to fit'),
        h('label', { class: 'settings-row' }, h('span', null, 'Delay (ms)'), delay),
        h(
          'div',
          { class: 'settings-row' },
          h('span', null, 'Repeat'),
          h('span', { class: 'settings-inline' }, repeat, h('label', null, forever, ' Infinite')),
        ),
        h(
          'p',
          { class: 'settings-note' },
          'Repeats the whole scene. Individual tracks can repeat on their own, or play only once, from the timeline (right-click a track).',
        ),
        this.playbackSummary(),
        h('h4', null, 'Direction'),
        this.radios('Direction', DIRECTIONS, scene.direction, (value) => {
          this.change({ direction: value });
        }),
        h(
          'details',
          { class: 'settings-advanced' },
          h('summary', null, 'Advanced'),
          h('h4', null, 'Fill mode'),
          this.radios(
            'Fill mode',
            FILLS.map(([v, l]): [Fill, string] => [v, l]),
            scene.fillMode,
            (value) => {
              this.change({ fillMode: value });
            },
          ),
        ),
        h('h4', null, 'Start animation'),
        this.radios('Start animation', startOptions, choiceOf(trigger), (value) => {
          this.chooseStart(value);
          this.render();
        }),
        this.triggerOptions(),
        h('h4', null, 'When visitors prefer reduced motion'),
        this.radios('Reduced motion', REDUCED, scene.reducedMotion, (value) => {
          this.change({ reducedMotion: value });
        }),
        h(
          'p',
          { class: 'settings-note' },
          'Some visitors turn off animations for comfort or accessibility. Respecting that setting is recommended.',
        ),
      ].filter((node): node is HTMLElement => node !== null),
    );
  }
}
